import type { ReactNode } from 'react';
import { Box } from '@mui/material';
import FormatAlignLeftIcon from '@mui/icons-material/FormatAlignLeft';
import FormatAlignCenterIcon from '@mui/icons-material/FormatAlignCenter';
import FormatAlignRightIcon from '@mui/icons-material/FormatAlignRight';
import FormatSizeIcon from '@mui/icons-material/FormatSize';
import RwIconButton from '@/common/presentation/components/rw/RwIconButton';
import { fieldStyleHasTextStyle } from '@/blocks/domain/helpers/fieldTextStyle';
import type { BlockField, BlockFieldAlign } from '@/blocks/domain/types';

export interface CanvasElementTextToolbarProps {
  field: BlockField;
  onPatch: (patch: Partial<BlockField>) => void;
  testId: string;
}

/** Styles printed in capitals whatever the switch says (`headingBlock`): a TT toggle would only mislead. */
const ALWAYS_UPPERCASE_STYLES: ReadonlySet<BlockField['style']> = new Set(['title', 'heading']);

const ALIGN_BUTTONS: Array<{ value: BlockFieldAlign; label: string; icon: ReactNode }> = [
  { value: 'left', label: 'Align left', icon: <FormatAlignLeftIcon fontSize="small" /> },
  { value: 'center', label: 'Align center', icon: <FormatAlignCenterIcon fontSize="small" /> },
  { value: 'right', label: 'Align right', icon: <FormatAlignRightIcon fontSize="small" /> }
];

/**
 * The inline toolbar of a selected text element (LLAMS-840), floating just below its frame: alignment, and the
 * uppercase switch (TT). These are the same `align` / `uppercase` patches the element settings
 * popover used to make, now made on the canvas where the text is edited.
 */
export function CanvasElementTextToolbar({ field, onPatch, testId }: CanvasElementTextToolbarProps) {
  const uppercase = field.uppercase === true;
  return (
    <Box
      role="toolbar"
      aria-label={`Text formatting - ${field.name || field.label || field.style}`}
      data-testid={`${testId}:toolbar`}
      onMouseDown={(event) => event.preventDefault()}
      sx={{
        // Absolutely positioned, like every other canvas addition: in flow it would grow the element and
        // push (or, on a fixed page sheet, clip) the content below it while it is selected.
        position: 'absolute',
        top: '100%',
        left: 0,
        zIndex: 2,
        display: 'flex',
        alignItems: 'center',
        gap: 'var(--size-4, 4px)',
        p: 'var(--size-4, 4px)',
        bgcolor: 'var(--surface-neutral-container-primary)',
        border: 'var(--border-width-sm, 1px) solid var(--border-focus)',
        borderTop: 0
      }}
    >
      {ALIGN_BUTTONS.map((option) => (
        <RwIconButton
          key={option.value}
          size="sm"
          aria-label={option.label}
          aria-pressed={field.align === option.value}
          onClick={() => onPatch({ align: option.value })}
          data-testid={`${testId}:align-${option.value}`}
        >
          {option.icon}
        </RwIconButton>
      ))}
      {fieldStyleHasTextStyle(field.style) && !ALWAYS_UPPERCASE_STYLES.has(field.style) ? (
        <RwIconButton
          size="sm"
          aria-label="Uppercase"
          aria-pressed={uppercase}
          onClick={() => onPatch({ uppercase: !uppercase })}
          data-testid={`${testId}:uppercase`}
        >
          <FormatSizeIcon fontSize="small" />
        </RwIconButton>
      ) : null}
    </Box>
  );
}
